// Pure selector helpers that read from the store data shape.

function toNumber(v) {
  if (v == null || v === '') return 0;
  const n = parseFloat(String(v).replace(/,/g, ''));
  return isNaN(n) ? 0 : n;
}

function normName(v) {
  return String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

// True when the cell opens with the name and the name ends at a word boundary,
// so "fatima - spousal" opens with "fatima" but "samuel" does not open with "sam".
function opensWithName(cell, name) {
  if (!name || !cell.startsWith(name)) return false;
  const next = cell.charAt(name.length);
  return next === '' || !/[\p{L}\p{N}]/u.test(next);
}

// Which client an income row's Owner names: 'client1', 'client2', or null when
// it names neither (a payer such as "CalSTRS", "Joint", "Trust").
//
// The Owner dropdown has offered first names only since 2026-08-05 (Jake), but
// the rules that gate Social Security and pensions by the owner's age went on
// matching the spouse by FULL name. A spouse's row picked from that very
// dropdown was therefore aged as client 1, so a younger spouse's unclaimed
// benefit counted as current income whenever client 1 was already past the
// starting age (Harley King, 2026-09-14: Fatima, 64, Social Security at 67,
// counted because Saad is 72). Owner is free text and advisors annotate it
// ("Fatima - Spousal ($20k)"), so a name opening the cell also counts.
//
// A full name beats a first name, nickname or lone surname, which beats a name
// that only opens the cell. A tie at the best level, such as two clients who
// share a first name with only that first name typed, names neither, and each
// caller's own fallback applies.
export function ownerPerson(family, owner) {
  const cell = normName(owner);
  if (!cell) return null;
  const f = family || {};
  const score = n => {
    const first = normName(f[`client${n}FirstName`]);
    const last = normName(f[`client${n}LastName`]);
    const nick = normName(f[`client${n}Nickname`]);
    const full = [first, last].filter(Boolean).join(' ');
    // The dropdown falls back to the surname when there is no first name.
    const short = [first || last, nick].filter(Boolean);
    if (full && cell === full) return 3;
    if (short.includes(cell)) return 2;
    if ([full, ...short].some(name => opensWithName(cell, name))) return 1;
    return 0;
  };
  const s1 = score(1);
  const s2 = score(2);
  if (s1 === s2) return null;
  return s1 > s2 ? 'client1' : 'client2';
}

function isFutureDate(dateStr) {
  if (!dateStr) return false;
  // Compare the full calendar date to today, not just the year. Comparing only
  // years let a start date later THIS year count as already-received (and made
  // "next year" the cutoff), so a future pension showed up in cash flow for up
  // to a year out (UAT: Gina). Parse YYYY-MM-DD by parts to avoid the timezone
  // shift `new Date('YYYY-MM-DD')` (UTC midnight) causes near day boundaries.
  const s = String(dateStr).trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  let y, mo, day;
  if (m) {
    y = +m[1]; mo = +m[2]; day = +m[3];
  } else {
    const d = new Date(s);
    if (isNaN(d.getTime())) return false;
    y = d.getFullYear(); mo = d.getMonth() + 1; day = d.getDate();
  }
  const now = new Date();
  const ty = now.getFullYear(), tm = now.getMonth() + 1, td = now.getDate();
  if (y !== ty) return y > ty;
  if (mo !== tm) return mo > tm;
  return day > td; // strictly after today; a start date of today counts as active
}

// The income rows that count toward *current* household income, per category.
// Social Security not yet claimed (startingAge above the owner's current age)
// and future-dated pension/other income are excluded — they aren't received
// yet. This is the single source of truth for "what income is active now": the
// edit-rail total, the dashboard metrics, and the Present statement all build
// on it, so a headline income figure can never disagree with the rows that
// make it up. Returned rows are the original objects (same references), so
// callers can read display fields (owner, description, dates) off them.
export function getIncludedIncomeRows(data) {
  const income = data?.income || {};
  const family = data?.family || {};
  const c1Age = toNumber(family.client1Age);
  const c2Age = toNumber(family.client2Age);

  // A row hasn't begun when its starting age is above the owner's current age.
  // ownerPerson matches the owner to a client, including by the first name the
  // Owner dropdown offers. An owner naming neither client falls back to client
  // 1's age, because a pension's owner is often the payer ("CalSTRS").
  const notYetByAge = (r, startAge) => {
    if (!(startAge > 0)) return false;
    const ownerAge = ownerPerson(family, r.owner) === 'client2' ? c2Age : c1Age;
    return startAge > ownerAge;
  };

  const employment = (income.employment || []).filter(Boolean);

  const socialSecurity = (income.socialSecurity || []).filter(
    r => r && !notYetByAge(r, toNumber(r.startingAge))
  );

  // Pensions honor Starting Age as well as Start Date: advisors fill whichever
  // is faster mid-appointment, and on the old PQ an age-only entry kept the
  // pension out of current income. Note the pension column key is startAge,
  // unlike Social Security's startingAge.
  const pension = (income.pension || []).filter(
    r => r && !isFutureDate(r.startDate) && !notYetByAge(r, toNumber(r.startAge))
  );
  const other = (income.other || []).filter(r => r && !isFutureDate(r.startDate));

  return { employment, socialSecurity, pension, other };
}

export function getTotalIncome(data) {
  const inc = getIncludedIncomeRows(data);
  let total = 0;
  for (const category of ['employment', 'socialSecurity', 'pension', 'other']) {
    inc[category].forEach(r => { total += toNumber(r.annualAmount); });
  }
  return total;
}

// The expense rows that count toward *current* household outflow. An expense
// that has not started yet is excluded, exactly as not-yet-claimed Social
// Security and future-dated pensions are kept out of current income.
//
// Advisors enter future costs while the client is in front of them (college
// starting 2028), and counting those today put a wrong number on screen mid
// appointment: "this expense isn't until 2028. It's factoring it in even though
// I put the start date of 2028" (Jake, 2026-08-05). The logic already existed
// on the income side and had simply never been applied here.
//
// Rows are still LISTED everywhere, including on the presented sheet. Only the
// totals are active-only, which is the same rule the income side follows.
//
// Deliberately NOT filtered on endDate: an expense that already ended is a
// separate question from one that has not started, and nobody has reported it.
export function getIncludedExpenseRows(data) {
  const te = data?.taxesExpenses || {};
  const rows = Array.isArray(te.expenses) ? te.expenses : [];
  return rows.filter(r => r && !isFutureDate(r.startDate));
}

export function getTotalExpenses(data) {
  const te = data?.taxesExpenses || {};
  let total = 0;
  getIncludedExpenseRows(data).forEach(r => {
    total += toNumber(r.amount);
  });
  total += toNumber(te.livingExpenses);
  return total;
}

export function getTotalTaxesPaid(data) {
  const te = data?.taxesExpenses || {};
  return toNumber(te.federalTax) + toNumber(te.stateTax) + toNumber(te.ficaTax);
}

// Effective tax rate = total tax / gross income (the "what share of income goes
// to tax" framing). Canonical definition used everywhere it's shown.
export function getEffectiveTaxRate(data) {
  const grossIncome = getTotalIncome(data);
  const totalTax = getTotalTaxesPaid(data);
  if (grossIncome <= 0 || totalTax <= 0) return null;
  return (totalTax / grossIncome) * 100;
}

function sumKey(rows, key) {
  if (!Array.isArray(rows)) return 0;
  return rows.reduce((s, r) => s + (r ? toNumber(r[key]) : 0), 0);
}

// A home's mortgage is the real-estate row's `remainingLoan`, surfaced as an
// auto-derived row in liabilities.items. If that derived row is ever missing
// (e.g. an older record reloaded without its _autoKey, the very bug that
// inflated net worth in QA), the loan would silently drop out of Total
// Liabilities and overstate Net Worth on a client-facing statement.
//
// This reconciles the two: it returns synthesized {description, amount} lines
// for any real-estate loan that is NOT already represented as a liability,
// so callers can both count it toward Total Liabilities and show it in a
// statement — without double-counting the derived rows that normally exist.
export function getUnbookedRealEstateLoans(assets, liabilityItems) {
  const re = Array.isArray(assets?.realEstate) ? assets.realEstate : [];
  const items = Array.isArray(liabilityItems) ? liabilityItems : [];
  const out = [];
  re.forEach(r => {
    const loan = toNumber(r?.remainingLoan);
    if (loan <= 0) return;
    const booked = items.some(it => {
      if (!it) return false;
      // The derived mortgage row keys off the asset's _autoKey...
      if (r._autoKey && it._reKey === r._autoKey) return true;
      // ...or, after a reload that stripped _reKey, a "…Mortgage" liability of
      // the same amount is the same loan.
      const desc = (it.description || '').trim().toLowerCase();
      return desc.endsWith('mortgage') && toNumber(it.amount) === loan;
    });
    if (!booked) {
      out.push({
        description: (r.description || 'Real Estate') + ' Mortgage',
        amount: loan,
        payment: toNumber(r?.payment), // monthly P&I, for cash-flow reconciliation
        // The loan still counts toward Total Liabilities either way; this only
        // tells the cash-flow side whether to charge the household for the
        // payment. See isRentalProperty.
        isRental: isRentalProperty(r),
        _reKey: r._autoKey || `re:${r._uid || r.description || loan}`,
      });
    }
  });
  return out;
}

// The inverse view of the reconciliation above: is this liability row a
// real-estate loan (the auto-derived mortgage of a property in Assets)? Uses
// the same two matching rules as getUnbookedRealEstateLoans so the two can
// never disagree: a live-session derived row carries _reKey; after a
// Salesforce reload strips it, a "…Mortgage" row whose amount equals a
// property's remaining loan is that property's mortgage. A manual mortgage
// with no matching property returns false (its loan appears nowhere else, so
// displays that filter on this must keep showing it).
// A real-estate row is a RENTAL when its Income (EBT) field holds any amount.
// Presence is the test, not sign or size: zero and negative both count, and an
// empty field does not.
//
// Why it matters: advisors enter that income figure NET, after the property's
// own costs are already taken out. So also counting the mortgage as a personal
// expense charges the household for it twice and drags cash flow down by the
// full debt service. Reported independently by Jake Greenberg (2026-08-05,
// "if there's income at all in there, it ignores the mortgage completely") and
// Joe Sanchez (2026-08-10, "the loan on the rental is showing up as an expense
// when it's being covered before the net income figure that I am using... the
// rental is making the shortfall look worse than it is"). The old PQ cancelled
// the liability out whenever the income field held anything.
export function isRentalProperty(reRow) {
  const raw = String(reRow?.incomeEBT ?? '').trim();
  if (raw === '') return false;
  return Number.isFinite(parseFloat(raw.replace(/,/g, '')));
}

// Is this liability row the mortgage of a RENTAL property? Uses the same two
// matching rules as getUnbookedRealEstateLoans / isRealEstateLoanRow, so the
// balance-sheet view and the cash-flow view can never disagree about which
// loan belongs to which property. The loan itself stays a liability either way;
// only its PAYMENT is kept out of the expense side.
export function isRentalLoanRow(row, assets) {
  if (!row) return false;
  const re = Array.isArray(assets?.realEstate) ? assets.realEstate : [];
  // Live session: the derived row carries the property's key.
  if (row._reKey) {
    const match = re.find(
      r => r && (r._autoKey === row._reKey || r._uid === row._reKey)
    );
    if (match) return isRentalProperty(match);
  }
  // After a Salesforce reload strips _reKey: a "…Mortgage" row whose amount
  // equals a property's remaining loan is that property's mortgage.
  const desc = (row.description || '').trim().toLowerCase();
  if (!desc.endsWith('mortgage')) return false;
  const amount = toNumber(row.amount);
  if (amount <= 0) return false;
  const match = re.find(r => toNumber(r?.remainingLoan) === amount);
  return match ? isRentalProperty(match) : false;
}

export function isRealEstateLoanRow(row, assets) {
  if (!row) return false;
  if (row._reKey) return true;
  const desc = (row.description || '').trim().toLowerCase();
  if (!desc.endsWith('mortgage')) return false;
  const re = Array.isArray(assets?.realEstate) ? assets.realEstate : [];
  const amount = toNumber(row.amount);
  return amount > 0 && re.some(r => toNumber(r?.remainingLoan) === amount);
}

const ASSET_TABLES = ['realEstate', 'taxDeferred', 'roth', 'taxable', 'cashCd', 'plan529', 'hsa', 'businessOther'];

// getDashboardMetrics is called several times per render (edit rail, KPI dock,
// Plan/Present views), each doing ~10 array reductions plus O(realEstate ×
// liabilities) reconciliation. The store replaces `data` immutably on every
// change, so a single-entry cache keyed on the `data` reference lets every
// caller in a given render reuse one computation and recompute only when the
// data actually changes.
let _metricsKey = null;
let _metricsVal = null;

export function getDashboardMetrics(data) {
  if (data === _metricsKey && _metricsVal) return _metricsVal;
  const result = computeDashboardMetrics(data);
  _metricsKey = data;
  _metricsVal = result;
  return result;
}

function computeDashboardMetrics(data) {
  const assets = data?.assets || {};
  const liabilities = data?.liabilities || {};
  const taxesExpenses = data?.taxesExpenses || {};

  const totalAssets = ASSET_TABLES.reduce((s, k) => s + sumKey(assets[k], 'marketValue'), 0);
  const realEstateAssets = sumKey(assets.realEstate, 'marketValue');
  const businessOtherAssets = sumKey(assets.businessOther, 'marketValue');
  const investableAssets = totalAssets - realEstateAssets - businessOtherAssets;

  // Personal additions only (savings); excludes company match.
  const totalSavings = ASSET_TABLES.reduce((s, k) => s + sumKey(assets[k], 'personalAdditions'), 0);

  const unbookedRE = getUnbookedRealEstateLoans(assets, liabilities.items);
  const unbookedRELoans = unbookedRE.reduce((s, l) => s + l.amount, 0);
  const totalLiabilities = sumKey(liabilities.items, 'amount') + unbookedRELoans;
  const totalNetWorth = totalAssets - totalLiabilities;

  const totalIncome = getTotalIncome(data);
  const totalTax = getTotalTaxesPaid(data);

  // Annual debt service comes from the liabilities themselves (monthly payment
  // × 12) plus any real-estate mortgage not booked as a liability. Computing it
  // from the liabilities — NOT from the auto-derived "From liabilities" expense
  // rows — keeps it correct on a cold load: the client-only `_source` tag does
  // not survive a Salesforce round-trip, so a tag-based split mis-counted DTI
  // and the debt/living breakdown until an edit re-tagged the rows.
  // A RENTAL property's mortgage is excluded here. The advisor's Income (EBT)
  // figure is already net of that payment, so charging it again double-counts
  // the debt service and understates cash flow (see isRentalProperty). The loan
  // BALANCE is untouched above and still counts toward Total Liabilities: it is
  // a real debt, it just isn't a personal expense.
  const liabItems = Array.isArray(liabilities.items) ? liabilities.items : [];
  const liabilityPayments =
    liabItems.reduce(
      (s, l) => s + (isRentalLoanRow(l, assets) ? 0 : toNumber(l?.payment) * 12),
      0
    ) +
    unbookedRE.reduce((s, l) => s + (l.isRental ? 0 : toNumber(l.payment) * 12), 0);

  // Living / other = the livingExpenses field plus every expense row that is
  // NOT liability-sourced. Those rows are identified by the round-trip-safe
  // `notes` marker OR the live `_source` tag, so the liability payments above
  // are never also counted here.
  // Not-yet-started rows are dropped here too (see getIncludedExpenseRows), so
  // an expense dated 2028 doesn't move today's cash flow.
  let nonLiabilityExpenses = toNumber(taxesExpenses.livingExpenses);
  const expenses = getIncludedExpenseRows(data);
  expenses.forEach(r => {
    if (!r) return;
    // `notes` is advisor-editable, so it can't be the only marker: rewriting
    // the note on the consolidated "Personal Liabilities" row would otherwise
    // double-count the household's entire debt service in cash flow. The
    // description IS read-only on derived rows, so it survives as a reliable
    // post-reload marker alongside the live _source tag.
    const desc = String(r.description || '').trim().toLowerCase();
    const liabSourced =
      (typeof r._source === 'string' && r._source.startsWith('liability')) ||
      String(r.notes || '').trim().toLowerCase() === 'from liabilities' ||
      desc === 'personal liabilities';
    if (!liabSourced) nonLiabilityExpenses += toNumber(r.amount);
  });

  const cashFlow = totalIncome - totalSavings - liabilityPayments - totalTax - nonLiabilityExpenses;

  // Debt-to-income is payments-based: annual debt service / gross income (the
  // lending-standard definition), not loan balances / income.
  const dti = totalIncome > 0 ? (liabilityPayments / totalIncome) * 100 : 0;
  const savingsRatio = totalIncome > 0 ? (totalSavings / totalIncome) * 100 : 0;

  // Tax triangle buckets
  const taxFree = sumKey(assets.roth, 'marketValue') + sumKey(assets.hsa, 'marketValue');
  const taxDeferred = sumKey(assets.taxDeferred, 'marketValue');
  const taxable = sumKey(assets.taxable, 'marketValue') + sumKey(assets.cashCd, 'marketValue');

  return {
    totalAssets, realEstateAssets, businessOtherAssets, investableAssets,
    totalLiabilities, totalNetWorth,
    totalIncome, totalSavings, totalTax,
    liabilityPayments, nonLiabilityExpenses, cashFlow,
    dti, savingsRatio,
    taxFree, taxDeferred, taxable,
  };
}

// Verdict thresholds for the key ratios. These live here, next to the metrics,
// so the edit rail and Present mode can never disagree on what counts as
// good / warn / bad (the Present view previously hardcoded its own cutoffs).
export function savingsRatioClass(r) {
  if (r >= 20) return 'good';
  if (r >= 10) return 'warn';
  return 'bad';
}

export function dtiClass(dti) {
  if (dti > 40) return 'bad';
  if (dti > 20) return 'warn';
  return 'good';
}

export function formatDollars(n) {
  if (n == null || isNaN(n)) return '$0';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return sign + '$' + (abs % 1 === 0
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
}

export function getOwnerNameOptions(data) {
  const family = data?.family || {};
  const flags = data?._flags || {};

  // First name only. Advisors read these in a narrow dropdown while a client
  // watches, and the surname is the same on both entries in most households, so
  // it carried no information (Jake, 2026-08-05). Falls back to the surname when
  // there is no first name, so an option is still offered. Ownership is a free
  // text datalist and its value is only ever displayed or passed through, so
  // rows already holding a full name keep it.
  const pick = (first, last) => (first || '').trim() || (last || '').trim();
  const c1 = pick(family.client1FirstName, family.client1LastName);
  const c2 = pick(family.client2FirstName, family.client2LastName);

  const opts = [];
  if (c1) opts.push(c1);
  if ((flags.hasSpouse || family.client2FirstName || family.client2LastName) && c2) {
    opts.push(c2);
  }
  if (c1 && c2) opts.push('Joint');
  opts.push('Trust');
  return opts;
}

// Owner options PLUS the children and grandchildren by name. Used for
// beneficiary / FBO fields (a 529's "for benefit of" is almost always a child,
// and beneficiaries on other accounts are often kids too) — John, 7/10, noted
// the 529 FBO only offered the client and spouse. Still a datalist, so any name
// can be typed over the suggestions.
export function getBeneficiaryNameOptions(data) {
  const family = data?.family || {};
  const kids = []
    .concat(Array.isArray(family.children) ? family.children : [])
    .concat(Array.isArray(family.grandchildren) ? family.grandchildren : [])
    .map(r => (r?.name || '').trim())
    .filter(Boolean);
  // De-dupe while preserving order (owners first, then kids).
  return [...new Set([...getOwnerNameOptions(data), ...kids])];
}
